import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SETTINGS } from "@/lib/content";
import { photoSet } from "@/lib/img";
import { Eyebrow, Reveal } from "./Reveal";

const Tile = ({ s, i }) => (
  <Reveal delay={i * 0.08} className={`${s.cls} h-72 sm:h-80 lg:h-auto`}>
    <motion.article whileHover="hover" className="group relative h-full rounded-[28px] overflow-hidden bg-[#154F49]" data-testid={`care-setting-${s.key}`}>
      <motion.img variants={{ hover: { scale: 1.06 } }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} src={s.img} srcSet={photoSet(s.img, 1264)} sizes={i === 0 ? "(min-width: 1024px) 58vw, 100vw" : "(min-width: 1024px) 42vw, 100vw"} decoding="async" alt={`${s.title} setting`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0D3834]/90 via-[#0D3834]/30 to-transparent" />
      <div className="relative h-full flex flex-col justify-end p-6 sm:p-8 text-[#FBF8F1]">
        <span className="text-xs tracking-[0.24em] uppercase text-[#E2C372]">0{i + 1}</span>
        <h3 className="font-serif text-3xl sm:text-4xl mt-1">{s.title}</h3>
        <p className="mt-2 text-sm sm:text-base text-[#EDF1E6] max-w-md">{s.copy}</p>
      </div>
      <span className="absolute top-5 right-5 w-11 h-11 rounded-full bg-[#FBF8F1]/15 backdrop-blur-md flex items-center justify-center text-[#FBF8F1] transition-transform duration-500 group-hover:rotate-45">
        <ArrowUpRight className="w-5 h-5" />
      </span>
    </motion.article>
  </Reveal>
);

export const CareSettings = () => (
  <section id="care-settings" className="py-20 sm:py-28 no-print" data-testid="care-settings-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="grid lg:grid-cols-12 gap-6 items-end mb-12">
        <div className="lg:col-span-7 space-y-4">
          <Eyebrow>Care settings</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#154F49] leading-[1.02]">Four settings. <em className="text-[#9B742A]">One standard</em> of care.</h2>
        </div>
        <p className="lg:col-span-5 text-base text-[#465F57] leading-relaxed">From a family living room to a busy hospital unit, every placement is matched to training, credentials, authorised scope and the agreed care plan.</p>
      </Reveal>
      <div className="grid grid-cols-1 lg:grid-cols-12 lg:auto-rows-[260px] gap-5">
        {SETTINGS.slice(0, 3).map((s, i) => <Tile key={s.key} s={s} i={i} />)}
        <Tile s={SETTINGS[3]} i={3} />
        <Reveal delay={0.3} className="lg:col-span-4">
          <div className="h-full rounded-[28px] bg-[#CDA34F] p-8 flex flex-col justify-between gap-6" data-testid="care-settings-cta-tile">
            <p className="font-serif text-3xl text-[#154F49] leading-tight">Not sure which setting fits?</p>
            <div className="space-y-3">
              <p className="text-sm text-[#154F49]">Call or email to confirm coverage and availability for your location.</p>
              <a href="#contact" className="inline-flex items-center gap-2 font-semibold text-[#154F49] underline underline-offset-4">Talk to Elevion <ArrowUpRight className="w-4 h-4" /></a>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
